/**
 * YabanciKatilimciKarti.jsx — Yabancı Katılımcı'nın Personel satırı (Deniz'in beyaz tahta çizimi, 2026-10-21).
 *
 * Çizim (soldan sağa), Kişi Listesi kartının (PersonelKart.jsx, #531) aynı tasarım ilkesi, Yabancı Katılımcı'ya göre kısaltılmış:
 *   [vesikalık + "Kart Durumu" hapı] [ad · pasaport no · telefon] [Pasaport · GDPR (SMS)] [firma adı · Çalışan · Görevlendir / Görevlendirmeyi Kaldır] [KKD]
 *   · Vesikalığın çerçevesi ile hap AYNI renkte ve o renk üç durumlu KART KARARI (`kartKarariUc`: yeşil Kart Alabilir · sarı Alanda Kart
 *     Alabilir · kırmızı Kart Alamaz; karar hesaplanmadıysa gri). Karar satırın fuar KADROSU ayrıntısından (`dto`) gelir — kadro dışı satırda
 *     ayrıntı okunmaz, gri kalır. Şerit YALNIZCA verilmiş Özel İzin'de (kırmızı), başka hiçbir durumda yok.
 *   · Firma adı oturumdaki firmanın kendi adıdır: kendi listesinde yazması fazlalık ama müşteri öyle istiyor (Deniz, 2026-10-21).
 *   · "Çalışan" / "İşveren" yazısının rengi kişinin bu fuardaki durumu — Kişi Listesi'ndeki rol rozetiyle AYNI renk dili (`firmaKatilimMeta`):
 *     yeşil Görevlendirildi · sarı Eşlendi (SAKA eşledi, firmanın cevabı bekleniyor) · kırmızı Eşleme reddedildi · gri bu fuarda görevli değil.
 *     `firmaKatilimMeta` firma girdisinin `aktifFuarda` / `aktifFuarEslesmeDurumu` alanlarını okur; burada satırın `fuarDurumu` /
 *     `eslesmeReddedildi` alanlarından kurulur (aynı anlam).
 *     İşveren mi çalışan mı yalnızca ayrıntıdan (`dto.owner`) bilinir; ayrıntısı olmayan satır "Çalışan" yazar.
 *   · Düğmeler: Görevlendir (yeşil, kişiyi üst çubuktaki fuara görevlendirir) · Görevlendirmeyi Kaldır (kırmızı, yalnızca fuar kadrosundan çıkarır;
 *     backend `DELETE /personnel/sil/{id}?fuarId&firmaId` portal PERSONEL WRITE'a bağlı — firma kendi kişisini çıkarabilir) · Onayla / Reddet
 *     (SAKA'nın alanda eşlediği kişi için; kaldırmak yerine Reddet çünkü cevap kaydedilir).
 *   · Yalnızca KKD diğer belgelerden kalan kare; SGK / İSG / Sağlık / Meslek / Firma evrakları bu kullanıcıdan istenmez (#511).
 *   · Sağ sütun: Detaylar, sönük "İlişkiyi Sonlandır" bağlantısı (kişiyi Ex-Employees'e taşır), sonlandırılmışta Geri Al.
 * Karar bayrakları (hangi düğme çıkar) FirmaPersonelSekmeleri.PersonelSekmeKarti'nda hesaplanır ve buraya hazır gelir.
 * #704 (2026-11-03): Sade panel modunda bu kart HER firmanın satırıdır. `belgeler` verilirse (null dahil) belge yuvasına Pasaport + GDPR yerine
 * o çizilir: firmanın kendi belge kareleri (FirmaPersonelSekmeleri.KendiBelgeKareleri). Verilmezse (gerçek Yabancı Katılımcı) eskisi gibi.
 */
import React from "react";
import { Badge, Card, CardBody, Col, Row, Button, Spinner } from "reactstrap";
import { Eye, Phone } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  PersonelAdBaglantisi, PersonelBelgeKareleri, IliskiBitisRozeti, KartKarariVesikalik, OzelIzinRibbon, RolRozeti,
} from "./PersonelKartParcalari";
import { EslesmeSatiri, EslesmeCevapDugmeleri } from "./PersonelDurumParcalari";
import { KartGorevlendirDugmesi, KartGorevKaldirDugmesi, KartSonlandirDugmesi, KartGeriAlDugmesi } from "./PersonelRosterEylemleri";
import { YabanciKatilimciBelgeKutulari } from "./YabanciKatilimciParcalari";

/** Sağ düğme sütununun genişliği (Detaylar · sönük sonlandır bağlantısı · Geri Al). */
const EYLEM_SUTUNU = { width: 150 };

/** #535: ortadaki sütunun iki sabit yuvasının en az genişliği (Pasaport + GDPR kareleri · KKD karesi); firma bloğu kalanı doldurur. */
const SLOT_BELGELER = 150;
const SLOT_KKD = 80;

/** #620: the name column's one width (name · ID number · phone), the same in every row. */
const NAD_SUTUNU = 240;

/**
 * Satırın bu fuardaki durumu → `firmaKatilimMeta`nın okuduğu alanlar. `fuarDurumu` yalnızca fuar seçiliyken gelir; fuar yoksa ya da kişi
 * görevli değilse "görevli değil" (gri). Reddedilmiş Eşleme `GOREVLENDIRILMEMIS` + `eslesmeReddedildi`dir.
 */
export function katilimAlanlari(row, fuarId) {
  if (!fuarId) return { aktifFuarda: false };
  if (row.fuarDurumu === "KADRODA") return { aktifFuarda: true, aktifFuarEslesmeDurumu: 1 };
  if (row.fuarDurumu === "ONAY_BEKLIYOR") return { aktifFuarda: true, aktifFuarEslesmeDurumu: 0 };
  if (row.eslesmeReddedildi) return { aktifFuarda: true, aktifFuarEslesmeDurumu: 2 };
  return { aktifFuarda: false };
}

export default function YabanciKatilimciKarti({
  row, dto, ad, firmaGorunenAdi, fuarId, busy, dim, bitis, bitisBilgisi, notlar,
  detayBekleniyor, detayHata, adBaglantisi, detayAcik, onDetay,
  cevaplanabilir, gorevlendirilebilir, gorevKaldirilabilir, sonlandirilabilir, geriAlinabilir, pasaportAcik,
  onPasaport, onGorevlendir, onGorevKaldir, onOnayla, onReddet, onSonlandir, onGeriAl,
  onFoto, fotoKilitli = false, fotoYukleniyor = false,
  belgeler,
}) {
  const { t } = useTranslation("components");
  const ozelIzin = dto?.singlePermit || null;
  const rolFirma = { firmaName: firmaGorunenAdi, isveren: dto?.owner === true, ...katilimAlanlari(row, fuarId) };
  // Pasaport numarası: kişinin kendi pasaport alanı (kimlik doğrulama bloğu), yoksa satırın kimlik no'su (yabancıda çoğu zaman aynı sütun).
  const kimlikNo = dto?.kimlikDogrulama?.pasaportNo || row.tcNo || row.kimlikNo || "—";
  const eylemVar = cevaplanabilir || gorevlendirilebilir || gorevKaldirilabilir;
  const iletisim = row.telefon ? t("personelSekmeler.iletisim", { telefon: row.telefon }) : null;

  return (
    <Card className="mb-3 position-relative overflow-hidden">
      {ozelIzin && <OzelIzinRibbon izin={ozelIzin} />}
      <CardBody className={ozelIzin ? "pt-4" : undefined}>
        <Row className="align-items-center g-3">
          <Col md="auto" style={dim}>
            <KartKarariVesikalik
              person={row} dto={dto} serit={Boolean(ozelIzin)} portal
              onFoto={onFoto} fotoKilitli={fotoKilitli} fotoYukleniyor={fotoYukleniyor}
            />
          </Col>

          {/* #535 (2026-10-21, Deniz): the row is stretched a little — wider name and company blocks, and the squares / company block spread over
              the free width instead of bunching on the left before a large gap. WAS: minWidth 170 / maxWidth 240, `gap-4` without fixed slots. */}
          {/* #620 (2026-10-30, Deniz): a long name used to wrap and stretch this column from 200 to 280 px, which pushed the middle slots onto a second line;
              now ONE fixed width, and the name / ID number / phone keep their line (ellipsis, the full text on hover). WAS: minWidth 200 / maxWidth 280. */}
          <Col md="auto" style={{ width: NAD_SUTUNU, maxWidth: "100%", minWidth: 0 }}>
            <div style={dim}>
              <div className="fw-bold fs-5 text-truncate" title={ad}>
                {adBaglantisi ? (
                  <PersonelAdBaglantisi href={adBaglantisi.href} onGit={adBaglantisi.onGit}>{ad}</PersonelAdBaglantisi>
                ) : ad}
              </div>
              <div className="text-muted small text-truncate" title={kimlikNo}>{kimlikNo}</div>
              {row.telefon && (
                <div className="text-muted d-flex align-items-center gap-1" style={{ fontSize: "0.75rem" }} title={iletisim}>
                  <Phone size={11} className="flex-shrink-0" /><span className="text-truncate">{iletisim}</span>
                </div>
              )}
              {notlar}
            </div>
            <div className="d-flex align-items-center gap-1 flex-wrap mt-1">
              {row.yasakli && <span className="badge bg-danger fw-normal">{t("personelSekmeler.girisiYasakli")}</span>}
              {row.kayitTuru === "ESKI_KAYIT" && (
                <Badge color="light" className="text-body border fw-normal" title={t("personelSekmeler.listedeDegilTitle")}>
                  {t("personelSekmeler.listedeDegil")}
                </Badge>
              )}
              <IliskiBitisRozeti record={bitis} />
            </div>
            {bitis && (
              <div className="small text-muted mt-1" style={{ whiteSpace: "normal" }}>
                {[bitisBilgisi.tarih, bitisBilgisi.bitirenAdi, bitisBilgisi.kaynakAdi].filter(Boolean).join(" · ")}
                {bitisBilgisi.sebep && <span className="fst-italic"> — {bitisBilgisi.sebep}</span>}
              </div>
            )}
            {/* Yalnızca SAKA'nın alanda eşlediği kişide (kim eşledi · cevap · red sebebi); firmanın kendi görevlendirmesi "Çalışan" yazısının rengiyle
                zaten okunur — "Görevlendirme: Onaylandı" satırı çizimde yok. */}
            {dto?.eslesmeTipi === 2 && <EslesmeSatiri record={dto} className="mt-2" />}
          </Col>

          {/* Three fixed slots (documents · company · KKD) spread over the free width: the same columns in every row, whether or not a row has
              the GDPR square or the KKD square. */}
          {/* #620: every width in the row is now fixed (photo 140, name 260, slots, actions), so whether the slots wrap is the same in every row;
              `minWidth: 0` lets the company slot give way instead of pushing the row wider. */}
          <Col className="d-flex flex-wrap align-items-center justify-content-between gap-3" style={{ minWidth: 0 }}>
            {/* #704: a company's own squares (up to six for a Yerli worker) share the line and wrap inside their slot, so the company block and
                the KKD square stay on the same line in every row; the Yabancı Katılımcı's two boxes keep the fixed slot. */}
            {/* #716: seen live at 1440 px, `1 1 0` squeezed the captioned squares into three short lines (KVKK alone, three, two); the slot now takes
                its own width (`0 1 auto`), so the squares share one line while there is room and the company block wraps instead. WAS: flex "1 1 0". */}
            <div style={belgeler !== undefined ? { flex: "0 1 auto", minWidth: SLOT_BELGELER } : { flex: "0 0 auto", minWidth: SLOT_BELGELER }}>
              {detayBekleniyor ? (
                detayHata
                  ? <span className="small text-muted">{t("personelSekmeler.belgeOkunamadi")}</span>
                  : <span className="small text-muted d-inline-flex align-items-center gap-2"><Spinner size="sm" color="secondary" />{t("personelSekmeler.belgeOkunuyor")}</span>
              ) : belgeler !== undefined ? belgeler : (
                // WAS (until #704): ) : ( — the slot always held the Yabancı Katılımcı's boxes.
                // Pasaport yazma uçları sonlanmış ilişkide 403 verir → `pasaportAcik` false iken tıklanamaz.
                <YabanciKatilimciBelgeKutulari
                  dto={dto} dogrulamaDurum={row.kimlikDogrulamaDurum} onPasaport={pasaportAcik ? onPasaport : undefined}
                />
              )}
            </div>

            {/* #620, seen live at 1366 px: name column 240 (WAS 260 in the first pass), gap-3 (WAS gap-4) and a 180 px company basis (WAS 200) so the three
                slots stay on ONE line at a laptop width; below that they wrap the same way in every row. */}
            <div style={{ flex: "1 1 180px", maxWidth: 360, minWidth: 0, ...dim }}>
              {/* WAS (until #620): className="fw-semibold text-break" - a long company name wrapped to a second line. */}
              <div className="fw-semibold text-truncate" style={{ fontSize: "0.95rem" }} title={firmaGorunenAdi || undefined}>{firmaGorunenAdi || "—"}</div>
              <RolRozeti firma={rolFirma} />
              {eylemVar && (
                <div className="d-flex flex-column align-items-start gap-1 mt-2">
                  {cevaplanabilir && (
                    <EslesmeCevapDugmeleri
                      record={{ eslesmeTipi: 2, eslesmeDurumu: row.fuarDurumu === "ONAY_BEKLIYOR" ? 0 : 2 }}
                      busy={busy} onOnayla={onOnayla} onReddet={onReddet}
                    />
                  )}
                  {gorevlendirilebilir && (
                    <KartGorevlendirDugmesi
                      disabled={busy} onClick={onGorevlendir}
                      title={t("personelSekmeler.fuarIcinGorevlendir", { fuarId })}
                    />
                  )}
                  {gorevKaldirilabilir && <KartGorevKaldirDugmesi disabled={busy} onClick={onGorevKaldir} />}
                </div>
              )}
            </div>

            <div className="d-flex justify-content-center" style={{ flex: "0 0 auto", minWidth: SLOT_KKD }}>
              {dto && <PersonelBelgeKareleri kkd={dto.kkd ?? null} bolum="diger" etiketli />}
            </div>
          </Col>

          <Col md="auto" className="d-flex align-items-center ms-auto mt-2 mt-md-0">
            <div className="d-flex flex-column align-items-stretch gap-1" style={EYLEM_SUTUNU}>
              {detayAcik && (
                <Button color="info" outline size="sm" className="w-100" onClick={onDetay}>
                  <Eye size={14} className="me-1" />{t("personelSekmeler.detaylar")}
                </Button>
              )}
              {/* #511: kırmızı düğme değil, sönük bir metin bağlantısı. */}
              {sonlandirilabilir && <KartSonlandirDugmesi disabled={busy} onClick={onSonlandir} sade />}
              {geriAlinabilir && <KartGeriAlDugmesi disabled={busy} onClick={onGeriAl} />}
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
}
